import React from "react"
import { REPORT_COLORS } from "./reportTheme"

// Renders the small subset of markdown these AI-generated descriptions
// actually use — **bold**, ## headings, --- dividers, - bullet lists —
// instead of dumping the raw symbols on the page.
const renderInline = text => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean)
  return parts.map((part, i) => {
    const m = part.match(/^\*\*(.+)\*\*$/)
    return m ? <strong key={i} style={{ color: REPORT_COLORS.textPrimary }}>{m[1]}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
  })
}

const groupBlocks = text => {
  const blocks = []
  let paragraph = []
  let list = null

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", text: paragraph.join(" ") })
      paragraph = []
    }
  }
  const flushList = () => {
    if (list) {
      blocks.push(list)
      list = null
    }
  }

  for (const rawLine of text.split("\n")) {
    const line = rawLine.trim()

    if (!line) {
      flushParagraph()
      flushList()
      continue
    }
    if (/^(-{3,}|\*{3,})$/.test(line)) {
      flushParagraph()
      flushList()
      blocks.push({ type: "hr" })
      continue
    }
    // Store listings sometimes embed video previews via markdown's image
    // syntax, with a JSON descriptor ({height,width,type:"video"}) stuffed
    // into the alt-text slot instead of real alt text. Videos are dropped
    // entirely rather than rendered; plain image embeds still show.
    const media = line.match(/^!\[([^\]]*)\]\((\S+)\)$/)
    if (media) {
      flushParagraph()
      flushList()
      let meta = {}
      try {
        meta = JSON.parse(media[1])
      } catch {
        meta = {}
      }
      const isVideo = meta.type === "video" || /\.(mp4|webm|mov)(\?|$)/i.test(media[2])
      if (!isVideo) blocks.push({ type: "media", url: media[2] })
      continue
    }
    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      flushParagraph()
      flushList()
      blocks.push({ type: "heading", level: heading[1].length, text: heading[2] })
      continue
    }
    const bullet = line.match(/^[-*]\s+(.*)$/)
    if (bullet) {
      flushParagraph()
      if (!list) list = { type: "list", items: [] }
      list.items.push(bullet[1])
      continue
    }
    flushList()
    paragraph.push(line)
  }
  flushParagraph()
  flushList()
  return blocks
}

export default function MarkdownLite({ text, style }) {
  if (!text) return null
  const blocks = groupBlocks(text)

  return (
    <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, lineHeight: 1.6, ...style }}>
      {blocks.map((block, i) => {
        if (block.type === "hr") {
          return <hr key={i} style={{ border: "none", borderTop: `1px solid ${REPORT_COLORS.cardBorder}`, margin: "12px 0" }} />
        }
        if (block.type === "media") {
          return (
            <div key={i} style={{ margin: "8px 0" }}>
              <img src={block.url} alt="" style={{ maxWidth: "100%", borderRadius: 8, display: "block" }} />
            </div>
          )
        }
        if (block.type === "heading") {
          return (
            <div key={i} style={{ fontSize: block.level <= 2 ? 14 : 13, fontWeight: 700, color: REPORT_COLORS.textPrimary, marginTop: i === 0 ? 0 : 12, marginBottom: 6 }}>
              {renderInline(block.text)}
            </div>
          )
        }
        if (block.type === "list") {
          return (
            <ul key={i} style={{ margin: "0 0 8px", paddingLeft: 18 }}>
              {block.items.map((item, j) => (
                <li key={j} style={{ marginBottom: 4 }}>{renderInline(item)}</li>
              ))}
            </ul>
          )
        }
        return (
          <p key={i} style={{ margin: "0 0 8px" }}>
            {renderInline(block.text)}
          </p>
        )
      })}
    </div>
  )
}
